import React, { useState } from "react";
import { Form, Input, Button, Card, message, Typography, Steps } from "antd";
import { UserOutlined, LockOutlined, MailOutlined, SafetyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // 0: credentials, 1: OTP
  const [email, setEmail] = useState("");
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleCredentialsSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await authService.login(values.email, values.password);
      console.log("Login response:", response);

      if (response?.requiresOTP) {
        setEmail(values.email);
        setCurrentStep(1);
        message.success("OTP sent to your email. Please check your inbox.");
      } else {
        message.error(response?.message || "Unexpected response");
      }
    } catch (error) {
      console.error("Login error:", error);
      message.error(
        error?.response?.data?.message || error.message || "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await authService.verifyOTP(email, values.otp);
      console.log("OTP verification response:", response);

      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("super_admin_token", token);
        message.success("Login successful");
        navigate("/users");
      } else {
        message.error(response?.message || "Verification failed");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      message.error(
        error?.response?.data?.message || error.message || "Verification failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const password = form.getFieldValue('password');
      const response = await authService.login(email, password);
      
      if (response?.requiresOTP) {
        message.success("New OTP sent to your email");
      }
    } catch (error) {
      message.error("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-form">
        <Title level={2} className="login-title">
          Super Admin Dashboard
        </Title>

        <Steps
          current={currentStep}
          style={{ marginBottom: 24 }}
          items={[
            {
              title: 'Credentials',
              icon: <UserOutlined />,
            },
            {
              title: 'Verification',
              icon: <SafetyOutlined />,
            },
          ]}
        />

        {currentStep === 0 ? (
          <Form
            form={form}
            name="credentials"
            onFinish={handleCredentialsSubmit}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input your password!" },
                { min: 8, message: "Password must be at least 8 characters!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Continue
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form
            name="otp"
            onFinish={handleOTPSubmit}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Text type="secondary">
                We've sent a 6-digit OTP to <strong>{email}</strong>
              </Text>
            </div>

            <Form.Item
              label="Enter OTP"
              name="otp"
              rules={[
                { required: true, message: "Please input the OTP!" },
                { len: 6, message: "OTP must be 6 digits!" },
                { pattern: /^[0-9]+$/, message: "OTP must contain only numbers!" },
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="000000"
                maxLength={6}
                style={{ fontSize: '20px', letterSpacing: '8px', textAlign: 'center' }}
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Verify & Sign In
              </Button>
            </Form.Item>

            <div style={{ textAlign: 'center' }}>
              <Button type="link" onClick={handleResendOTP} disabled={loading}>
                Resend OTP
              </Button>
              <span style={{ margin: '0 8px' }}>|</span>
              <Button type="link" onClick={() => setCurrentStep(0)} disabled={loading}>
                Back to Login
              </Button>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default Login;
